import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
// import { apiEndpoints, handleApiError } from '../../services/api';
import { createApiUrlWithPrefix, createApiUrl, getUserImageUrl } from '../../config/api';
import { useUserCounts } from '../../contexts/UserCountsContext';
import { useSettings } from '../../contexts/SettingsContext';
import { toast } from 'react-hot-toast';
import {
  Search,
  UserPlus,
  Edit,
  Trash2,
  RefreshCw,
  Eye,
  Zap,
  ChevronLeft,
  ChevronRight,
  CreditCard,
  Calendar,
  CheckCircle,
  XCircle,
  MoreVertical,
  X,
  Plus,
  DollarSign,
  AlertTriangle,
  Shield
} from 'lucide-react';
import UserCreationModal from '../UserCreationModal';
import UserEditModal from '../UserEditModal';
import UserDetailsModal from '../UserDetailsModal';
import HorseManagementModal from '../HorseManagementModal';

function Owners() {
  const { t, i18n } = useTranslation();
  const { refreshUserCounts } = useUserCounts();
  const { getSettingAsNumber } = useSettings();
  const basicPrice = getSettingAsNumber('owner.subscription.basic', 40000);
  const mediumPrice = getSettingAsNumber('owner.subscription.medium', 60000);
  const luxuryPrice = getSettingAsNumber('owner.subscription.luxury', 80000);
  const isRTL = i18n.language === 'ar';

  // State management
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(12);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [viewingUser, setViewingUser] = useState(null);
  const [showHorseModal, setShowHorseModal] = useState(false);
  const [selectedOwner, setSelectedOwner] = useState(null);
  const [subscriptions, setSubscriptions] = useState({});
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [statusOwner, setStatusOwner] = useState(null);
  const [dropdownOpen, setDropdownOpen] = useState(null); // Track which dropdown is open
  const [ownerStatus, setOwnerStatus] = useState({
    subscription: null,
    forfaits: [],
    horses: [],
    loading: false
  });
  const [showHorseStatusModal, setShowHorseStatusModal] = useState(false);
  const [loadingHorseStatus, setLoadingHorseStatus] = useState(false);
  const [horseStatusData, setHorseStatusData] = useState([]);
  const [ownerPaymentStatuses, setOwnerPaymentStatuses] = useState({});
  const [showOwnerPaymentModal, setShowOwnerPaymentModal] = useState(false);
  const [ownerPaymentForm, setOwnerPaymentForm] = useState({
    amount: '',
    subscriptionTier: 1,
    description: '',
    horseId: null,
    selectedMonths: [{ year: new Date().getFullYear(), month: new Date().getMonth() + 1 }]
  });
  const [submittingPayment, setSubmittingPayment] = useState(false);

    const [refreshKey, setRefreshKey] = useState(0);

  const getTierPrice = (tier) => {
    switch (tier) {
      case 1: return basicPrice;
      case 2: return mediumPrice;
      case 3: return luxuryPrice;
      default: return 0;
    }
  };

  const getTierLabel = (tier) => {
    switch (tier) {
      case 1: return 'Basic';
      case 2: return 'Medium';
      case 3: return 'Luxury';
      default: return 'Unknown';
    }
  };

  const openOwnerPaymentModal = async (user) => {
    setSelectedOwner(user);
    setOwnerPaymentForm({
      amount: String(basicPrice),
      subscriptionTier: 1,
      description: getTierLabel(1),
      horseId: null,
      selectedMonths: [{ year: new Date().getFullYear(), month: new Date().getMonth() + 1 }]
    });
    setShowOwnerPaymentModal(true);
  };

  const handleCreateOwnerPayment = async (e) => {
    e.preventDefault();
    if (!ownerPaymentForm.amount || parseFloat(ownerPaymentForm.amount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }
    if (!ownerPaymentForm.horseId) {
      toast.error('Please select a horse');
      return;
    }
    setSubmittingPayment(true);
    try {
      const currentUserId = localStorage.getItem('userId');
      if (!currentUserId) {
        toast.error('Session expired. Please login again.');
        return;
      }
      const paymentData = {
        ownerId: selectedOwner.id,
        amount: parseFloat(ownerPaymentForm.amount),
        subscriptionTier: parseInt(ownerPaymentForm.subscriptionTier),
        notes: ownerPaymentForm.description,
        horseId: ownerPaymentForm.horseId,
        selectedMonths: ownerPaymentForm.selectedMonths,
        processedBy: parseInt(currentUserId),
        isHistorical: false
      };
      await axios.post(createApiUrlWithPrefix('OwnerPayment/create'), paymentData, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });
      toast.success('Payment created successfully');
      setShowOwnerPaymentModal(false);
      await fetchUsers();
    } catch (error) {
      console.error('Error creating owner payment:', error);
      toast.error(error.response?.data?.message || 'Failed to create payment');
    } finally {
      setSubmittingPayment(false);
    }
  };

// Fetch subscription status for an owner
  const fetchOwnerSubscription = useCallback(async (ownerId) => {
    try {
      const response = await axios.get(createApiUrlWithPrefix(`OwnerPayment/subscription/${ownerId}`), {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      return response.data;
    } catch (error) {
      // Owner might not have a subscription, return null
      return null;
    }
  }, []);

  // Fetch owner forfaits/subscriptions
  const fetchOwnerForfaits = useCallback(async (ownerId) => {
    try {
      const response = await axios.get(createApiUrlWithPrefix(`OwnerPayment/forfaits/${ownerId}`), {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      return response.data || [];
    } catch (error) {
      console.error('Error fetching owner forfaits:', error);
      return [];
    }
  }, []);

  // Fetch horses for an owner
  const fetchOwnerHorses = useCallback(async (ownerId) => {
    try {
      const response = await axios.get(createApiUrlWithPrefix(`horses?ownerId=${ownerId}`), {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      return response.data || [];
    } catch (error) {
      console.error('Error fetching owner horses:', error);
      return [];
    }
  }, []);

  // Fetch owners
  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: itemsPerPage.toString(),
        role: '1', // Owner role
        _t: Date.now(), // Cache busting parameter
        ...(searchTerm && { search: searchTerm })
      });
      
      const response = await axios.get(createApiUrlWithPrefix(`users?${params}`), {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      const owners = response.data.users || response.data;
      setUsers(owners);
      setTotalPages(response.data.totalPages || 1);
      setTotalItems(response.data.totalItems || response.data.length);

      // Fetch subscription status, forfaits, and horses for each owner
      const ownerDataPromises = owners.map(async (owner) => {
        const [subscription, forfaits, horses] = await Promise.all([
          fetchOwnerSubscription(owner.id),
          fetchOwnerForfaits(owner.id),
          fetchOwnerHorses(owner.id)
        ]);
        return { ownerId: owner.id, subscription, forfaits, horses };
      });

      const ownerDataResults = await Promise.all(ownerDataPromises);
      const subscriptionMap = {};
      const forfaitsMap = {};
      const horsesMap = {};
      ownerDataResults.forEach(({ ownerId, subscription, forfaits, horses }) => {
        subscriptionMap[ownerId] = subscription;
        forfaitsMap[ownerId] = forfaits;
        horsesMap[ownerId] = horses;
      });
      setSubscriptions(subscriptionMap);

      // Fetch payment statuses (total paid, debt) for all owners in bulk
      const riderIds = owners.map(r => r.id);
      const bulkStatusResponse = await axios.post(
        createApiUrlWithPrefix('OwnerPayment/bulk-status'),
        riderIds,
        {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        }
      ).catch(err => {
        console.error('Error fetching owner payment statuses:', err);
        return { data: {} };
      });
      setOwnerPaymentStatuses(bulkStatusResponse.data || {});
    } catch (error) {
      console.error('Failed to fetch owners:', error);
      toast.error('Failed to load owners');
    } finally {
      setLoading(false);
    }
  }, [currentPage, itemsPerPage, searchTerm, fetchOwnerSubscription, fetchOwnerForfaits, fetchOwnerHorses]);

  // Get subscription tier name
  const getTierName = (tier) => {
    switch (tier) {
      case 1: return 'Basic';
      case 2: return 'Medium';
      case 3: return 'Luxury';
      default: return 'Unknown';
    }
  };

  // Get subscription tier color
  const getTierColor = (tier) => {
    switch (tier) {
      case 1: return 'bg-blue-100 text-blue-800';
      case 2: return 'bg-green-100 text-green-800';
      case 3: return 'bg-purple-100 text-purple-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  // Handle viewing owner status
  const handleViewOwnerStatus = async (user) => {
    setStatusOwner(user);
    setShowStatusModal(true);
    setDropdownOpen(null);
    setOwnerStatus({ subscription: null, forfaits: [], horses: [], loading: true });
    
    try {
      // Fetch subscription status, forfaits, and horses
      const [subscriptionResponse, forfaitsResponse, horsesResponse] = await Promise.all([
        axios.get(createApiUrlWithPrefix(`OwnerPayment/subscription/${user.id}`), {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        }).catch(() => null),
        axios.get(createApiUrlWithPrefix(`OwnerPayment/forfaits/${user.id}`), {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        }).catch(() => null),
        axios.get(createApiUrlWithPrefix(`horses?ownerId=${user.id}`), {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        }).catch(() => null)
      ]);

      setOwnerStatus({
        subscription: subscriptionResponse?.data || null,
        forfaits: Array.isArray(forfaitsResponse?.data) ? forfaitsResponse.data : [],
        horses: Array.isArray(horsesResponse?.data) ? horsesResponse.data : [],
        loading: false
      });
    } catch (error) {
      console.error('Error fetching owner status:', error);
      setOwnerStatus({ 
        subscription: null, 
        forfaits: [], 
        horses: [], 
        loading: false 
      });
      toast.error('Failed to fetch owner status');
    }
  };

  const handleCheckHorses = async (owner) => {
    try {
      setSelectedOwner(owner);
      setLoadingHorseStatus(true);
      setShowHorseStatusModal(true);
      
      const [forfaits, horses] = await Promise.all([
        axios.get(createApiUrlWithPrefix(`OwnerPayment/forfaits/${owner.id}`), {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        }),
        axios.get(createApiUrlWithPrefix(`horses?ownerId=${owner.id}`), {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        })
      ]);

      const forfaitsData = forfaits.data || [];
      const horsesData = horses.data?.horses || horses.data || [];
      
      const currentYear = new Date().getFullYear();
      const currentMonth = new Date().getMonth() + 1;

      const subscriptionTypes = [
        { value: 1, label: 'Basic' },
        { value: 2, label: 'Medium' },
        { value: 3, label: 'Luxury' }
      ];

      const processedStatus = horsesData.map(horse => {
        const horseForfaits = forfaitsData.filter(f => f.horseId === horse.id);
        const isCurrentMonthPaid = horseForfaits.some(f => f.year === currentYear && f.month === currentMonth);
        const paidMonthsCount = horseForfaits.length;
        const currentForfait = horseForfaits.sort((a, b) => (b.year * 100 + b.month) - (a.year * 100 + a.month))[0];

        return {
          ...horse,
          isCurrentMonthPaid,
          paidMonthsCount,
          currentForfait,
          forfaitLabel: currentForfait ? (subscriptionTypes.find(t => t.value === currentForfait.tier)?.label || currentForfait.tier) : null
        };
      });

      setHorseStatusData(processedStatus);
    } catch (error) {
      console.error('Error fetching horse status:', error);
      toast.error('Failed to fetch horse status data.');
    } finally {
      setLoadingHorseStatus(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownOpen && !event.target.closest('.dropdown-container')) {
        setDropdownOpen(null);
      }
    };

    if (dropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [dropdownOpen]);

  // Listen for the custom event from sidebar
  useEffect(() => {
    const handleOpenAddUserModal = (event) => {
      if (event.detail.role === 'Owner' || event.detail.role === 1) {
        setShowModal(true);
      }
    };

    window.addEventListener('openAddUserModal', handleOpenAddUserModal);
    return () => {
      window.removeEventListener('openAddUserModal', handleOpenAddUserModal);
    };
  }, []);

  // Event handlers
  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setSearchTerm('');
    setCurrentPage(1);
  };

  const handleSelectUser = (userId) => {
    setSelectedUsers(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const handleSelectAll = () => {
    if (selectedUsers.length === users.length) {
      setSelectedUsers([]);
    } else {
      setSelectedUsers(users.map(user => user.id));
    }
  };

  const handleUserCreated = async () => {
    console.log('🔄 User created, refreshing list...');
    await fetchUsers(); // Refresh the list
    setRefreshKey(prev => prev + 1); // Force component refresh
    console.log('✅ Users list refreshed after creation');
  
    // Refresh sidebar counts
    refreshUserCounts();
  };

  // Horse operation handlers
  const handleHorseCreated = async () => {
    console.log('🔄 Horse created, refreshing data...');
    await fetchUsers(); // Refresh the owners list
    setRefreshKey(prev => prev + 1); // Force component refresh
    console.log('✅ Data refreshed after horse creation');
  };

  const handleHorseUpdated = async () => {
    console.log('🔄 Horse updated, refreshing data...');
    await fetchUsers(); // Refresh the owners list
    setRefreshKey(prev => prev + 1); // Force component refresh
    console.log('✅ Data refreshed after horse update');
  };

  const handleHorseDeleted = async () => {
    console.log('🔄 Horse deleted, refreshing data...');
    await fetchUsers(); // Refresh the owners list
    setRefreshKey(prev => prev + 1); // Force component refresh
    console.log('✅ Data refreshed after horse deletion');
  };

  const handleEditUser = (user) => {
    setEditingUser(user);
    setShowEditModal(true);
    setDropdownOpen(null);
  };

  const handleDropdownToggle = (userId) => {
    setDropdownOpen(dropdownOpen === userId ? null : userId);
  };

  const handleAction = (action, user) => {
    setDropdownOpen(null);
    switch (action) {
      case 'view':
        handleViewUserDetails(user);
        break;
      case 'status':
        handleViewOwnerStatus(user);
        break;
      case 'edit':
        handleEditUser(user);
        break;
      case 'delete':
        handleDeleteUser(user.id);
        break;
      case 'horses':
        handleManageHorses(user);
        break;
      case 'payments':
        window.open('/payments/owners', '_blank');
        break;
      default:
        break;
    }
  };

  const handleViewUserDetails = (user) => {
    setViewingUser(user);
    setShowDetailsModal(true);
  };

  const handleManageHorses = (owner) => {
    setSelectedOwner(owner);
    setShowHorseModal(true);
  };

  const handleDeleteUser = async (userId) => {
    if (window.confirm('Are you sure you want to delete this user? This action cannot be undone.')) {
      try {
        await axios.delete(createApiUrlWithPrefix(`users/${userId}`), {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        toast.success('User deleted successfully');
        
        // Check if we need to go back a page after deletion
        const currentUsersCount = users.length;
        console.log('📄 Current users count:', currentUsersCount, 'Current page:', currentPage);
        
        if (currentUsersCount === 1 && currentPage > 1) {
          console.log('⬅️ Going back to previous page');
          const newPage = Math.max(currentPage - 1, 1);
          setCurrentPage(newPage);
          // Don't call fetchUsers here - let useEffect handle it when currentPage changes
        } else {
          // Only refresh immediately if we're not changing pages
          console.log('🔄 Refreshing users list after delete...');
          await fetchUsers();
        }
        
        // Force component refresh
        setRefreshKey(prev => prev + 1);
        console.log('✅ Users list refreshed after delete');
        
        // Refresh sidebar counts
        refreshUserCounts();
      } catch (error) {
        console.error('Failed to delete user:', error);
        console.error('Error response:', error.response?.data);
        console.error('Error status:', error.response?.status);
        
        // Extract error message from various possible locations
        let errorMessage = 'Failed to delete user';
        
        if (error.response?.data) {
          const errorData = error.response.data;
          if (errorData.message) {
            errorMessage = errorData.message;
          } else if (errorData.error) {
            errorMessage = errorData.error;
          } else if (typeof errorData === 'string') {
            errorMessage = errorData;
          } else if (errorData.detail) {
            errorMessage = errorData.detail;
          }
        } else if (error.message) {
          errorMessage = error.message;
        }
        
        // Check for specific error types
        if (error.response?.status === 403) {
          toast.error('Access denied: You do not have permission to delete users. Admin or SuperAdmin role required.');
        } else if (error.response?.status === 401) {
          toast.error('Unauthorized: Please log in again.');
        } else if (error.response?.status === 404) {
          toast.error('User not found.');
        } else {
          toast.error(errorMessage);
        }
      }
    }
  };

  const handleUserUpdated = () => {
    setShowEditModal(false);
    setEditingUser(null);
    fetchUsers();
    // Refresh sidebar counts (in case role changed)
    refreshUserCounts();
  };

  // Print badge function - removed as unused
  /*
  const handlePrintBadge = async (user) => {
    try {
      const response = await axios.get(createApiUrlWithPrefix(`users/${user.id}/qr-code`), {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      const qrData = response.data.userGuid || response.data.qrCode || `EC_${user.id}`;
      
      // Debug logging
      console.log('User data for badge:', {
        firstName: user.firstName,
        lastName: user.lastName,
        birthDate: user.birthDate,
        bloodType: user.bloodType,
        qrData: qrData
      });
      
      // Calculate age from birth date (commented out as not used)
      // const age = user.birthDate && user.birthDate !== '0001-01-01T00:00:00' 
      //   ? new Date().getFullYear() - new Date(user.birthDate).getFullYear()
      //   : 'N/A';
      
      // Format birth date
      const birthDate = user.birthDate && user.birthDate !== null
        ? new Date(user.birthDate).toLocaleDateString('en-US', {
            year: 'numeric',
            month: '2-digit', 
            day: '2-digit'
          })
        : 'Not provided';
      
      // Create badge HTML for printing
      const printWindow = window.open('', '_blank');
      printWindow.document.write(`
        <html>
          <head>
            <title>Badge - ${user.firstName} ${user.lastName}</title>
            <style>
              body { 
                font-family: 'Segoe UI', Arial, sans-serif; 
                margin: 0; 
                padding: 20px; 
                background: white;
                display: flex;
                justify-content: center;
                align-items: center;
                min-height: 100vh;
              }
              .badge { 
                width: 500px; 
                height: 420px; 
                border: 2px solid #2c3e50; 
                margin: 0 auto;
                background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
                border-radius: 15px;
                box-shadow: 0 4px 8px rgba(0,0,0,0.1);
                position: relative;
                overflow: hidden;
              }
              .header { 
                background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
                color: white;
                padding: 15px 25px;
                text-align: center;
                font-weight: bold;
                font-size: 18px;
                letter-spacing: 1px;
              }
              .content { 
                padding: 25px;
                display: flex;
                gap: 20px;
                height: calc(100% - 70px);
              }
              .left-section { 
                flex: 1;
                display: flex;
                flex-direction: column;
                gap: 10px;
              }
              .user-photo { 
                width: 100px; 
                height: 100px; 
                border-radius: 50%; 
                border: 3px solid #3b82f6;
                object-fit: cover;
                margin-bottom: 15px;
              }
              .user-photo-placeholder { 
                width: 100px; 
                height: 100px; 
                border-radius: 50%; 
                border: 3px solid #3b82f6;
                background: linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%);
                display: flex;
                align-items: center;
                justify-content: center;
                font-weight: bold;
                font-size: 32px;
                color: #3b82f6;
                margin-bottom: 15px;
              }
              .user-name { 
                font-size: 22px; 
                font-weight: bold; 
                color: #2c3e50;
                margin-bottom: 8px;
              }
              .user-details { 
                font-size: 14px; 
                color: #5a6c7d;
                line-height: 1.6;
              }
              .detail-item { 
                margin-bottom: 8px;
                display: flex;
                align-items: center;
                gap: 8px;
              }
              .right-section { 
                flex: 1;
                display: flex;
                flex-direction: column;
                align-items: center;
                justify-content: center;
              }
              .qr-container { 
                width: 140px; 
                height: 140px; 
                border: 2px solid #3b82f6;
                border-radius: 10px;
                display: flex;
                flex-direction: column;
                align-items: center;
                justify-content: center;
                background: white;
                margin-bottom: 15px;
              }
              .qr-code { 
                width: 120px;
                height: 120px;
                display: flex;
                align-items: center;
                justify-content: center;
              }
              .qr-label { 
                font-size: 12px; 
                color: #3b82f6;
                font-weight: bold;
                text-align: center;
                margin-bottom: 10px;
              }
              .role-badge { 
                background: linear-gradient(135deg, #10b981 0%, #059669 100%);
                color: white;
                padding: 6px 16px;
                border-radius: 20px;
                font-size: 12px;
                font-weight: bold;
                text-transform: uppercase;
                letter-spacing: 0.5px;
              }
              @media print {
                body { margin: 0; padding: 0; }
                .badge { margin: 0; }
              }
            </style>
          </head>
          <body>
            <div class="badge">
              <div class="header">EQUESTRIAN CLUB MEMBER</div>
              <div class="content">
                <div class="left-section">
                  ${(() => {
                    const imageUrl = getUserImageUrl(user);
                    return imageUrl ? 
                      `<img src="${imageUrl}" alt="User Photo" class="user-photo" onerror="this.style.display='none'; this.nextElementSibling.style.display='flex';" />` : 
                      '';
                  })()}
                  <div class="user-photo-placeholder" style="${getUserImageUrl(user) ? 'display: none;' : 'display: flex;'}">
                    ${user.firstName?.charAt(0)?.toUpperCase() || 'U'}
                  </div>
                  
                  <div class="user-name">${user.firstName} ${user.lastName}</div>
                  <div class="user-details">
                    <div class="detail-item">📅 Birth Date: ${birthDate}</div>
                    <div class="detail-item">🩸 Blood Type: ${user.bloodType || 'N/A'}</div>
                  </div>
                </div>
                
                <div class="right-section">
                  <div class="qr-container">
                    <div class="qr-code" id="qr-code-${user.id}"></div>
                  </div>
                  <div class="qr-label">SCAN QR CODE</div>
                  <div class="role-badge">OWNER</div>
                </div>
              </div>
            </div>
            <script src="https://cdn.jsdelivr.net/npm/qrcode@1.5.3/build/qrcode.min.js"></script>
            <script>
              // Generate QR code after page loads
              function generateQRCode() {
                const qrElement = document.getElementById('qr-code-${user.id}');
                console.log('QR Element found:', qrElement);
                console.log('QRCode library available:', typeof QRCode !== 'undefined');
                console.log('QR Data:', '${qrData}');
                
                if (qrElement && typeof QRCode !== 'undefined') {
                  try {
                    // Create a canvas element
                    const canvas = document.createElement('canvas');
                    qrElement.appendChild(canvas);
                    
                    QRCode.toCanvas(canvas, '${qrData}', {
                      width: 120,
                      height: 120,
                      margin: 1,
                      color: { 
                        dark: '#2c3e50', 
                        light: '#ffffff' 
                      },
                      errorCorrectionLevel: 'M'
                    }, function (error) {
                      if (error) {
                        console.error('QR Code generation failed:', error);
                        qrElement.innerHTML = '<div style="color: #2c3e50; font-size: 10px; text-align: center; padding: 10px; background: #f0f0f0; border: 1px solid #ccc; border-radius: 5px;">QR CODE<br/>${qrData}</div>';
                      } else {
                        console.log('QR Code generated successfully');
                        // Make sure the canvas is visible
                        canvas.style.display = 'block';
                        canvas.style.margin = '0 auto';
                      }
                    });
                  } catch (err) {
                    console.error('QR Code generation error:', err);
                    qrElement.innerHTML = '<div style="color: #2c3e50; font-size: 10px; text-align: center; padding: 10px; background: #f0f0f0; border: 1px solid #ccc; border-radius: 5px;">QR CODE<br/>${qrData}</div>';
                  }
                } else {
                  console.error('QR Element not found or QRCode library not loaded');
                  if (qrElement) {
                    qrElement.innerHTML = '<div style="color: #2c3e50; font-size: 10px; text-align: center; padding: 10px; background: #f0f0f0; border: 1px solid #ccc; border-radius: 5px;">QR CODE<br/>${qrData}</div>';
                  }
                }
              }
              
              // Try multiple times to ensure QRCode library is loaded
              let attempts = 0;
              const maxAttempts = 10;
              
              function tryGenerateQR() {
                if (typeof QRCode !== 'undefined') {
                  generateQRCode();
                } else if (attempts < maxAttempts) {
                  attempts++;
                  setTimeout(tryGenerateQR, 200);
                } else {
                  console.error('QRCode library failed to load after multiple attempts');
                  const qrElement = document.getElementById('qr-code-${user.id}');
                  if (qrElement) {
                    qrElement.innerHTML = '<div style="color: #2c3e50; font-size: 10px; text-align: center; padding: 10px; background: #f0f0f0; border: 1px solid #ccc; border-radius: 5px;">QR CODE<br/>${qrData}</div>';
                  }
                }
              }
              
              // Start trying to generate QR code
              tryGenerateQR();
            </script>
          </body>
        </html>
      `);
      
      printWindow.document.close();
      printWindow.print();
      
      toast.success('Badge opened for printing');
    } catch (error) {
      console.error('Failed to print badge:', error);
      toast.error('Failed to print badge');
    }
  };
  */

  /* removed early loading return to prevent search bar unmounting 
if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  } 
*/

  return (
    <>
      <div className="flex flex-col min-h-full bg-neutral-50 pb-8" dir={isRTL ? 'rtl' : 'ltr'} key={refreshKey}>
      {/* Fixed Header Section */}
      <div className="px-4 sm:px-6 lg:px-8 py-6">
      {/* Header - Responsive */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">{t('owners.title')}</h1>
          <p className="mt-2 sm:mt-3 text-sm sm:text-base text-gray-600">
            {t('owners.subtitle')}
          </p>
        </div>
        <button
          onClick={() => setShowModal(true)}
          className="bg-primary-600 text-white px-3 py-2 sm:px-4 rounded-lg hover:bg-primary-700 transition-colors flex items-center min-h-[44px] min-w-[44px] touch-manipulation text-sm sm:text-base"
        >
          <UserPlus className="w-4 h-4 mr-2" />
          <span className="hidden sm:inline">{t('owners.addOwner')}</span>
        </button>
      </div>

      {/* Search Bar */}
      <div className="bg-white rounded-xl shadow-sm border border-neutral-200 p-4 sm:p-6 mt-6">
        <div className="flex flex-col lg:flex-row gap-4 items-center">
          {/* Search Input */}
          <div className="flex-1 w-full">
            <div className="relative">
              <Search className={`absolute top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400 ${isRTL ? 'right-4' : 'left-4'}`} />
              <input
                type="text"
                placeholder={t('owners.searchPlaceholder')}
                value={searchTerm}
                onChange={handleSearch}
                className={`w-full py-3 px-10 text-base sm:text-lg border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200 ${isRTL ? 'pr-11 pl-4' : 'pl-11 pr-4'}`}
              />
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center space-x-3 w-full lg:w-auto">
            <button
              onClick={clearFilters}
              className="flex-1 lg:flex-none justify-center px-6 py-3 text-gray-600 border border-gray-300 rounded-xl hover:bg-gray-50 transition-colors font-medium"
            >
              {t('common.clear')}
            </button>
            
            <button
              onClick={fetchUsers}
              className="flex-1 lg:flex-none flex justify-center items-center px-6 py-3 text-gray-600 border border-gray-300 rounded-xl hover:bg-gray-50 transition-colors"
            >
              <RefreshCw className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>
      {/* End of Fixed Header Section */}

      {/* Scrollable Cards Section */}
      <div className="px-4 sm:px-6 lg:px-8 pb-8">
        <div className="space-y-6">
          {/* Select All and Owners Cards */}
          <div className="space-y-4">
            {/* Select All Checkbox */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <input
                type="checkbox"
                checked={selectedUsers.length === users.length && users.length > 0}
                onChange={handleSelectAll}
                className="rounded border-gray-300 text-primary-600 focus:ring-primary-500 mr-3"
              />
              <span className="text-sm font-medium text-gray-700">
                {t('common.selectAll')} ({selectedUsers.length} {t('common.of')} {users.length} {t('common.selected')})
              </span>
            </div>
            {selectedUsers.length > 0 && (
              <div className="flex items-center space-x-2">
                <span className="text-sm text-gray-500">
                  {selectedUsers.length} owner{selectedUsers.length !== 1 ? 's' : ''} selected
                </span>
                <button className="px-3 py-1 text-sm text-red-600 hover:text-red-800 hover:bg-red-50 rounded-lg transition-colors">
                  Delete Selected
                </button>
              </div>
            )}
          </div>
        </div>
        
        {/* Owners Cards Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-6">
          {users.map((user) => (
            <div key={user.id} className="relative bg-white rounded-lg shadow-md border border-gray-200 hover:shadow-xl transition-all duration-300 overflow-hidden group">
              {/* Header with checkbox and role badge */}
              <div className="flex items-center justify-between p-4 pb-2">
                <input
                  type="checkbox"
                  checked={selectedUsers.includes(user.id)}
                  onChange={() => handleSelectUser(user.id)}
                  className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-bold bg-green-100 text-green-800">
                  Owner
                </span>
              </div>
              
              {/* User Info */}
              <div className="px-4 pb-4">
                {/* Avatar */}
                <div className="flex justify-center mb-4">
                  {getUserImageUrl(user) ? (
                    <div className="relative">
                      <img
                        src={getUserImageUrl(user)}
                        alt={user.name || 'Owner'}
                        className="w-16 h-16 rounded-full object-cover border-2 border-green-100 shadow-sm"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          if (e.target.nextSibling) e.target.nextSibling.style.display = 'flex';
                        }}
                      />
                      <div className="w-16 h-16 rounded-full bg-gradient-to-br from-green-100 to-green-200 flex items-center justify-center border-2 border-green-100 shadow-sm hidden">
                        <span className="text-xl font-bold text-green-600">
                          {user.name?.charAt(0)?.toUpperCase() || 'O'}
                        </span>
                      </div>
                    </div>
                  ) : (
                    <div className="w-16 h-16 rounded-full bg-gradient-to-br from-green-100 to-green-200 flex items-center justify-center border-2 border-green-100 shadow-sm">
                      <span className="text-xl font-bold text-green-600">
                        {user.name?.charAt(0)?.toUpperCase() || 'O'}
                      </span>
                    </div>
                  )}
                </div>
                
                {/* Name and Email */}
                <div className="text-center mb-4">
                  <h3 className="text-lg font-semibold text-gray-900 truncate">
                    {user.name || t('common.unknownOwner')}
                  </h3>
                  <p className="text-sm text-gray-500 truncate">{user.email}</p>
                </div>
                
                {/* Payment Status & Debt */}
                <div className="space-y-2 mb-3">
                  {/* Total Paid This Year */}
                  <div className="bg-blue-50 rounded-lg p-2.5 border border-blue-200">
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center gap-1.5">
                        <DollarSign className="w-3.5 h-3.5 text-blue-600" />
                        <span className="text-[10px] font-medium text-blue-800">Paid (This Year)</span>
                      </div>
                      <span className="text-xs font-bold text-blue-700">
                        {(ownerPaymentStatuses[user.id]?.totalPaidThisYear || 0).toLocaleString()} DA
                      </span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <Shield className="w-3 h-3 text-blue-500" />
                      <span className="text-[10px] text-blue-600">
                        {ownerPaymentStatuses[user.id]?.horsesCount || 0} horse{(ownerPaymentStatuses[user.id]?.horsesCount || 0) !== 1 ? 's' : ''}
                        {' · '}
                        {ownerPaymentStatuses[user.id]?.activeSubscriptionsCount || 0} subscription{(ownerPaymentStatuses[user.id]?.activeSubscriptionsCount || 0) !== 1 ? 's' : ''}
                      </span>
                    </div>
                  </div>

                  {/* Debt Warning */}
                  {(ownerPaymentStatuses[user.id]?.totalDebt || 0) > 0 && (
                    <div className="bg-red-50 rounded-lg p-2.5 border border-red-300">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-1.5">
                          <AlertTriangle className="w-3.5 h-3.5 text-red-600" />
                          <span className="text-[10px] font-bold text-red-800">Outstanding Debt</span>
                        </div>
                        <span className="text-xs font-bold text-red-700">
                          {(ownerPaymentStatuses[user.id]?.totalDebt || 0).toLocaleString()} DA
                        </span>
                      </div>
                    </div>
                  )}

                  {/* Quick Pay Button */}
                  <button
                    onClick={() => openOwnerPaymentModal(user)}
                    className="w-full bg-green-600 text-white py-2 rounded-lg text-xs font-bold hover:bg-green-700 transition-all flex items-center justify-center gap-1.5"
                  >
                    <CreditCard className="w-3.5 h-3.5" />
                    Record Payment
                  </button>
                </div>

                {/* Check Horses Button */}
                <div className="pt-3 border-t border-gray-100">
                  <button
                    onClick={() => handleCheckHorses(user)}
                    className="w-full bg-white text-blue-600 border border-blue-600 py-3 rounded-xl text-sm font-bold hover:bg-blue-50 transition-all duration-200 flex items-center justify-center gap-2 group-hover:shadow-md"
                  >
                    <Eye className="w-4 h-4" />
                    {i18n.language === 'ar' ? 'فحص الخيول و حالة الإشتراك' : 'Check Horses & Subscription'}
                  </button>
                </div>
              </div>
              
              {/* Action Dropdown - Top Right */}
              <div className="absolute top-4 right-4 dropdown-container">
                <button
                  onClick={() => handleDropdownToggle(user.id)}
                  className="flex items-center justify-center w-8 h-8 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
                  title="Actions"
                >
                  <MoreVertical className="w-4 h-4" />
                </button>
                
                {dropdownOpen === user.id && (
                  <div className="absolute right-0 top-10 z-[9999] w-48 bg-white rounded-lg shadow-xl border border-gray-200 py-1 max-h-64 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
                    <button
                      onClick={() => handleAction('view', user)}
                      className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                    >
                      <Eye className="w-4 h-4 mr-3" />
                      {t('common.view')}
                    </button>
                    <button
                      onClick={() => handleAction('status', user)}
                      className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                    >
                      <Calendar className="w-4 h-4 mr-3" />
                      {t('common.status')}
                    </button>
                    <button
                      onClick={() => handleAction('edit', user)}
                      className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                    >
                      <Edit className="w-4 h-4 mr-3" />
                      {t('common.edit')}
                    </button>
                    <button
                      onClick={() => handleAction('horses', user)}
                      className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                    >
                      <Zap className="w-4 h-4 mr-3" />
                      {t('common.horses')}
                    </button>
                    <button
                      onClick={() => handleAction('payments', user)}
                      className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                    >
                      <CreditCard className="w-4 h-4 mr-3" />
                      {t('common.payments')}
                    </button>
                    <button
                      onClick={() => handleAction('delete', user)}
                      className="flex items-center w-full px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
                    >
                      <Trash2 className="w-4 h-4 mr-3" />
                      {t('common.delete')}
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
          </div>
        </div>
        </div>
      </div>
      {/* End of Scrollable Cards Section */}

      {/* Pagination - Sticky at bottom */}
      <div className="mt-6 bg-white rounded-xl shadow-sm border border-gray-200 p-4 sm:p-6">
        <div className="flex flex-col sm:flex-row items-center justify-between gap-3 sm:gap-4">
          <div className="text-xs sm:text-sm text-gray-700 font-medium">
            Showing <span className="font-bold text-blue-600">{((currentPage - 1) * itemsPerPage) + 1}</span> to <span className="font-bold text-blue-600">{Math.min(currentPage * itemsPerPage, totalItems)}</span> of <span className="font-bold text-blue-600">{totalItems}</span> results
          </div>
          
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              className="flex items-center px-3 py-2 sm:px-4 border border-gray-300 rounded-lg text-xs sm:text-sm font-medium text-gray-700 hover:bg-gray-50 hover:border-gray-400 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white transition-all duration-200 min-h-[44px] touch-manipulation"
            >
              <ChevronLeft className="w-4 h-4 mr-1" />
              <span className="hidden sm:inline">Previous</span>
              <span className="sm:hidden">Prev</span>
            </button>
            
            <div className="flex items-center space-x-1">
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                const pageNum = Math.max(1, Math.min(totalPages - 4, currentPage - 2)) + i;
                if (pageNum > totalPages) return null;
                
                return (
                  <button
                    key={pageNum}
                    onClick={() => setCurrentPage(pageNum)}
                    className={`px-3 py-2 text-xs sm:text-sm font-medium rounded-lg transition-all duration-200 min-h-[44px] min-w-[44px] touch-manipulation ${
                      currentPage === pageNum
                        ? 'bg-blue-600 text-white shadow-lg'
                        : 'text-gray-700 hover:bg-gray-100 border border-gray-300'
                    }`}
                  >
                    {pageNum}
                  </button>
                );
              })}
            </div>
            
            <button
              onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
              disabled={currentPage === totalPages}
              className="flex items-center px-3 py-2 sm:px-4 border border-gray-300 rounded-lg text-xs sm:text-sm font-medium text-gray-700 hover:bg-gray-50 hover:border-gray-400 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white transition-all duration-200 min-h-[44px] touch-manipulation"
            >
              <span className="hidden sm:inline">Next</span>
              <span className="sm:hidden">Next</span>
              <ChevronRight className="w-4 h-4 ml-1" />
            </button>
          </div>
        </div>
      </div>

      {/* User Creation Modal */}
      <UserCreationModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        role="Owner"
        onUserCreated={handleUserCreated}
      />
      
      {/* User Edit Modal */}
      {editingUser && (
        <UserEditModal
          isOpen={showEditModal}
          onClose={() => {
            setShowEditModal(false);
            setEditingUser(null);
          }}
          user={editingUser}
          onUserUpdated={handleUserUpdated}
          disableRoleEdit={true}
        />
      )}
      
      {viewingUser && (
        <UserDetailsModal
          isOpen={showDetailsModal}
          onClose={() => {
            setShowDetailsModal(false);
            setViewingUser(null);
          }}
          user={viewingUser}
        />
      )}
      
      {selectedOwner && (
        <HorseManagementModal
          isOpen={showHorseModal}
          onClose={() => {
            setShowHorseModal(false);
            setSelectedOwner(null);
          }}
          owner={selectedOwner}
          onHorseCreated={handleHorseCreated}
          onHorseUpdated={handleHorseUpdated}
          onHorseDeleted={handleHorseDeleted}
        />
      )}

      {/* Owner Status Modal */}
      {showStatusModal && statusOwner && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[80vh] overflow-hidden">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold text-gray-900">
                  Subscription Status - {statusOwner.firstName} {statusOwner.lastName}
                </h2>
                <button
                  onClick={() => {
                    setShowStatusModal(false);
                    setStatusOwner(null);
                    setOwnerStatus({ subscription: null, forfaits: [], loading: false });
                  }}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <XCircle className="w-6 h-6" />
                </button>
              </div>
            </div>
            
            <div className="p-6 overflow-y-auto max-h-[60vh]">
              {ownerStatus.loading ? (
                <div className="text-center py-8">
                  <RefreshCw className="w-8 h-8 text-gray-400 mx-auto mb-4 animate-spin" />
                  <p className="text-gray-500">Loading status...</p>
                </div>
              ) : (
                <div className="space-y-6 p-4 sm:p-6 lg:p-8">
                  {/* Current Subscription */}
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
                    <div className="flex items-center mb-4">
                      <CreditCard className="w-6 h-6 text-blue-600 mr-3" />
                      <h3 className="text-lg font-semibold text-blue-900">Current Subscription</h3>
                    </div>
                    {ownerStatus.subscription ? (
                      <div className="space-y-3">
                        <div className="flex items-center justify-between">
                          <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getTierColor(ownerStatus.subscription.tier)}`}>
                            {getTierName(ownerStatus.subscription.tier)} Subscription
                          </span>
                          <span className="text-lg font-semibold text-gray-900">
                            {ownerStatus.subscription.monthlyAmount?.toLocaleString()} DA/month
                          </span>
                        </div>
                        <div className="grid grid-cols-2 gap-4 text-sm">
                          <div>
                            <span className="text-gray-600">Max Horses:</span>
                            <span className="ml-2 font-medium">{ownerStatus.subscription.maxHorses}</span>
                          </div>
                          <div>
                            <span className="text-gray-600">Tier:</span>
                            <span className="ml-2 font-medium">{getTierName(ownerStatus.subscription.tier)}</span>
                          </div>
                          <div>
                            <span className="text-gray-600">Stabling:</span>
                            <span className="ml-2 font-medium">{ownerStatus.subscription.includesStabling ? '✅' : '❌'}</span>
                          </div>
                          <div>
                            <span className="text-gray-600">Feeding:</span>
                            <span className="ml-2 font-medium">{ownerStatus.subscription.includesFeeding ? '✅' : '❌'}</span>
                          </div>
                          <div>
                            <span className="text-gray-600">Grooming:</span>
                            <span className="ml-2 font-medium">{ownerStatus.subscription.includesGrooming ? '✅' : '❌'}</span>
                          </div>
                          <div>
                            <span className="text-gray-600">Veterinary:</span>
                            <span className="ml-2 font-medium">{ownerStatus.subscription.includesVeterinaryCare ? '✅' : '❌'}</span>
                          </div>
                          <div>
                            <span className="text-gray-600">Farrier:</span>
                            <span className="ml-2 font-medium">{ownerStatus.subscription.includesFarrierServices ? '✅' : '❌'}</span>
                          </div>
                          <div>
                            <span className="text-gray-600">Exercise:</span>
                            <span className="ml-2 font-medium">{ownerStatus.subscription.includesExercise ? '✅' : '❌'}</span>
                          </div>
                        </div>
                        <div className="text-sm text-gray-500">
                          Expires: {new Date(ownerStatus.subscription.expiryDate).toLocaleDateString()}
                        </div>
                      </div>
                    ) : (
                      <div className="text-center py-4">
                        <XCircle className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                        <p className="text-gray-500">No active subscription</p>
                      </div>
                    )}
                  </div>

                  {/* Horse Care Subscriptions - Grouped by Horse */}
                  <div className="bg-green-50 border border-green-200 rounded-lg p-6">
                    <div className="flex items-center mb-4">
                      <Calendar className="w-6 h-6 text-green-600 mr-3" />
                      <h3 className="text-lg font-semibold text-green-900">Horse Care Subscriptions by Horse</h3>
                    </div>
                    {ownerStatus.forfaits && ownerStatus.forfaits.length > 0 ? (
                      <div className="space-y-6 p-4 sm:p-6 lg:p-8">
                        {(() => {
                          // Group subscriptions by horse
                          const groupedByHorse = ownerStatus.forfaits.reduce((acc, subscription) => {
                            const horseId = subscription.horseId;
                            if (!acc[horseId]) {
                              acc[horseId] = [];
                            }
                            acc[horseId].push(subscription);
                            return acc;
                          }, {});

                          return Object.entries(groupedByHorse).map(([horseId, subscriptions]) => {
                            const horse = Array.isArray(ownerStatus.horses) ? ownerStatus.horses.find(h => h.id === parseInt(horseId)) : null;
                            const horseName = horse ? horse.name : `${t('common.horseNumber')}${horseId}`;
                            const horseBreed = horse?.breed || '';

                            return (
                              <div key={horseId} className="bg-white rounded-lg border border-gray-200 p-4">
                                {/* Horse Header */}
                                <div className="flex items-center gap-3 mb-4 pb-3 border-b border-gray-200">
                                  <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                                    <span className="text-2xl">🐎</span>
                                  </div>
                                  <div>
                                    <h4 className="font-semibold text-gray-900 text-lg">{horseName}</h4>
                                    {horseBreed && (
                                      <p className="text-sm text-gray-600">{horseBreed}</p>
                                    )}
                                    <p className="text-xs text-gray-500">Horse ID: {horseId}</p>
                                  </div>
                                </div>

                                {/* Subscriptions for this horse */}
                                <div className="space-y-3">
                                  {subscriptions.map((subscription, index) => {
                                    const isCurrentMonth = subscription.year === new Date().getFullYear() && 
                                                         subscription.month === new Date().getMonth() + 1;
                                    const isExpired = new Date(subscription.expiryDate) < new Date();
                                    const isFuture = subscription.year > new Date().getFullYear() || 
                                                   (subscription.year === new Date().getFullYear() && subscription.month > new Date().getMonth() + 1);
                                    
                                    return (
                                      <div key={subscription.id || index} className={`border rounded-lg p-3 ${
                                        isCurrentMonth ? 'bg-green-50 border-green-200' : 
                                        isExpired ? 'bg-red-50 border-red-200' : 
                                        isFuture ? 'bg-blue-50 border-blue-200' : 
                                        'bg-gray-50 border-gray-200'
                                      }`}>
                                        <div className="flex justify-between items-start mb-2">
                                          <div>
                                            <div className="flex items-center gap-2">
                                              <span className="font-medium text-gray-900">
                                                {new Date(subscription.year, subscription.month - 1).toLocaleDateString('en-US', { 
                                                  month: 'long', 
                                                  year: 'numeric' 
                                                })}
                                              </span>
                                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                                isCurrentMonth ? 'bg-green-200 text-green-800' : 
                                                isExpired ? 'bg-red-200 text-red-800' : 
                                                isFuture ? 'bg-blue-200 text-blue-800' : 
                                                'bg-gray-200 text-gray-800'
                                              }`}>
                                                {isCurrentMonth ? 'Current' : 
                                                 isExpired ? 'Expired' : 
                                                 isFuture ? 'Future' : 'Past'}
                                              </span>
                                            </div>
                                            <div className="flex items-center gap-4 mt-1">
                                              <span className="text-sm text-gray-600">
                                                {getTierName(subscription.tier)} - {subscription.monthlyAmount?.toLocaleString()} DA
                                              </span>
                                            </div>
                                          </div>
                                        </div>
                                        
                                        {/* Services included */}
                                        <div className="grid grid-cols-3 gap-2 text-xs">
                                          <div className="flex items-center gap-1">
                                            <span className="text-gray-600">Stabling:</span>
                                            <span>{subscription.includesStabling ? '✅' : '❌'}</span>
                                          </div>
                                          <div className="flex items-center gap-1">
                                            <span className="text-gray-600">Feeding:</span>
                                            <span>{subscription.includesFeeding ? '✅' : '❌'}</span>
                                          </div>
                                          <div className="flex items-center gap-1">
                                            <span className="text-gray-600">Grooming:</span>
                                            <span>{subscription.includesGrooming ? '✅' : '❌'}</span>
                                          </div>
                                          <div className="flex items-center gap-1">
                                            <span className="text-gray-600">Veterinary:</span>
                                            <span>{subscription.includesVeterinaryCare ? '✅' : '❌'}</span>
                                          </div>
                                          <div className="flex items-center gap-1">
                                            <span className="text-gray-600">Farrier:</span>
                                            <span>{subscription.includesFarrierServices ? '✅' : '❌'}</span>
                                          </div>
                                          <div className="flex items-center gap-1">
                                            <span className="text-gray-600">Exercise:</span>
                                            <span>{subscription.includesExercise ? '✅' : '❌'}</span>
                                          </div>
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            );
                          });
                        })()}
                      </div>
                    ) : (
                      <div className="text-center py-4">
                        <Calendar className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                        <p className="text-gray-500">No subscriptions found</p>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      {/* Horse Status Modal */}
      {showHorseStatusModal && selectedOwner && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
            <div className="p-4 border-b border-gray-100 flex items-center justify-between bg-blue-50">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                  <span className="text-xl">🐎</span>
                </div>
                <div>
                  <h3 className="font-bold text-gray-900">
                    {i18n.language === 'ar' ? 'خيول' : 'Horses for'} {selectedOwner.firstName} {selectedOwner.lastName}
                  </h3>
                  <p className="text-xs text-blue-600 font-medium">
                    {i18n.language === 'ar' ? 'فحص حالة الاشتراك لكل حصان' : 'Check subscription status for each horse'}
                  </p>
                </div>
              </div>
              <button 
                onClick={() => setShowHorseStatusModal(false)}
                className="p-2 hover:bg-blue-100 rounded-full transition-colors"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            <div className="overflow-y-auto p-4 space-y-4">
              {loadingHorseStatus ? (
                <div className="flex flex-col items-center justify-center py-12 space-y-3">
                  <RefreshCw className="w-8 h-8 text-blue-500 animate-spin" />
                  <p className="text-sm text-gray-500 font-medium">
                    {i18n.language === 'ar' ? 'جاري تحميل البيانات...' : 'Loading horse data...'}
                  </p>
                </div>
              ) : horseStatusData.length > 0 ? (
                horseStatusData.map(horse => (
                  <div key={horse.id} className="border border-gray-200 rounded-xl p-4 hover:border-blue-300 transition-colors shadow-sm">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 bg-gray-50 border border-gray-100 rounded-lg flex items-center justify-center text-2xl">
                          🐎
                        </div>
                        <div>
                          <h4 className="font-bold text-gray-900">{horse.name}</h4>
                          <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">{horse.breed || 'Unknown Breed'}</span>
                        </div>
                      </div>
                      
                      <div className={`px-3 py-1.5 rounded-full text-xs font-black flex items-center gap-2 border-2 ${
                        horse.isCurrentMonthPaid 
                          ? 'bg-green-50 border-green-200 text-green-700' 
                          : 'bg-red-50 border-red-200 text-red-700'
                      }`}>
                        {horse.isCurrentMonthPaid ? <CheckCircle className="w-3.5 h-3.5" /> : <XCircle className="w-3.5 h-3.5" />}
                        {horse.isCurrentMonthPaid 
                          ? (i18n.language === 'ar' ? 'الشهر الحالي مدفوع' : 'Current Month Paid') 
                          : (i18n.language === 'ar' ? 'الشهر الحالي غير مدفوع' : 'Current Month UNPAID')
                        }
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4 mt-4 bg-gray-50 p-3 rounded-lg">
                      <div className="flex flex-col">
                        <span className="text-[10px] uppercase text-gray-500 font-bold tracking-wider">
                          {i18n.language === 'ar' ? 'الاشتراك الحالي' : 'Current Forfait'}
                        </span>
                        <span className="text-sm font-bold text-gray-800">
                          {horse.forfaitLabel || (i18n.language === 'ar' ? 'لا يوجد' : 'No Active Forfait')}
                        </span>
                      </div>
                      <div className="flex flex-col border-l border-gray-200 pl-4">
                        <span className="text-[10px] uppercase text-gray-500 font-bold tracking-wider">
                          {i18n.language === 'ar' ? 'إجمالي الأشهر المدفوعة' : 'Total Paid Months'}
                        </span>
                        <span className="text-sm font-black text-blue-600">
                          {horse.paidMonthsCount} {i18n.language === 'ar' ? 'أشهر' : 'Months'}
                        </span>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-12">
                  <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <XCircle className="w-8 h-8 text-gray-400" />
                  </div>
                  <p className="text-gray-500 font-medium">
                    {i18n.language === 'ar' ? 'لم يتم العثور على خيول لهذا المشترك' : 'No horses found for this owner.'}
                  </p>
                </div>
              )}
            </div>

            <div className="p-4 border-t border-gray-100 bg-gray-50">
              <button
                onClick={() => setShowHorseStatusModal(false)}
                className="w-full bg-blue-600 text-white font-bold py-3 rounded-xl hover:bg-blue-700 transition-colors shadow-md"
              >
                {t('common.close')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Owner Payment Modal */}
      {showOwnerPaymentModal && selectedOwner && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md max-h-[90vh] overflow-hidden flex flex-col">
            <div className="p-4 border-b border-gray-100 flex items-center justify-between bg-green-50">
              <div className="flex items-center gap-3">
                <CreditCard className="w-5 h-5 text-green-600" />
                <h3 className="font-bold text-gray-900">
                  Record Payment — {selectedOwner.firstName} {selectedOwner.lastName}
                </h3>
              </div>
              <button onClick={() => setShowOwnerPaymentModal(false)} className="p-2 hover:bg-green-100 rounded-full transition-colors">
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            <form onSubmit={handleCreateOwnerPayment} className="p-4 space-y-4 overflow-y-auto">
              {/* Subscription Tier */}
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Subscription Tier</label>
                <div className="grid grid-cols-3 gap-2">
                  {[1, 2, 3].map(tier => (
                    <button
                      key={tier}
                      type="button"
                      onClick={() => setOwnerPaymentForm(prev => ({
                        ...prev,
                        subscriptionTier: tier,
                        amount: String(getTierPrice(tier)),
                        description: getTierLabel(tier)
                      }))}
                      className={`p-2 rounded-lg text-xs font-bold border-2 transition-all ${
                        ownerPaymentForm.subscriptionTier === tier
                          ? getTierColor(tier) + ' border-current'
                          : 'bg-gray-50 border-gray-200 text-gray-600 hover:bg-gray-100'
                      }`}
                    >
                      {getTierLabel(tier)}
                      <br />
                      <span className="text-[10px] opacity-75">{getTierPrice(tier).toLocaleString()} DA</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Amount */}
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">
                  Amount Paid <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <input
                    type="number"
                    value={ownerPaymentForm.amount}
                    onChange={(e) => setOwnerPaymentForm(prev => ({ ...prev, amount: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-green-500"
                    required
                  />
                </div>
                {parseFloat(ownerPaymentForm.amount || '0') < getTierPrice(ownerPaymentForm.subscriptionTier) && (
                  <p className="text-xs text-orange-600 mt-1 font-medium">
                    ⚠ Partial payment — {(getTierPrice(ownerPaymentForm.subscriptionTier) - parseFloat(ownerPaymentForm.amount || '0')).toLocaleString()} DA will be tracked as debt
                  </p>
                )}
                <p className="text-[10px] text-gray-500 mt-1">
                  Full price: {getTierPrice(ownerPaymentForm.subscriptionTier).toLocaleString()} DA
                </p>
              </div>

              {/* Horse Selection — need to fetch */}
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">
                  Select Horse <span className="text-red-500">*</span>
                </label>
                <OwnerHorseSelector ownerId={selectedOwner.id} value={ownerPaymentForm.horseId} onChange={(horseId) => setOwnerPaymentForm(prev => ({ ...prev, horseId }))} />
              </div>

              {/* Month — current month by default */}
              <div className="bg-gray-50 rounded-lg p-2.5 border border-gray-200">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-medium text-gray-700">Month</span>
                  <span className="text-sm font-bold text-gray-800">
                    {new Date(ownerPaymentForm.selectedMonths[0]?.year || new Date().getFullYear(), (ownerPaymentForm.selectedMonths[0]?.month || new Date().getMonth() + 1) - 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                  </span>
                </div>
              </div>

              {/* Description */}
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Description</label>
                <input
                  type="text"
                  value={ownerPaymentForm.description}
                  onChange={(e) => setOwnerPaymentForm(prev => ({ ...prev, description: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                />
              </div>

              {/* Submit */}
              <button
                type="submit"
                disabled={submittingPayment}
                className="w-full bg-green-600 text-white font-bold py-3 rounded-xl hover:bg-green-700 disabled:opacity-50 transition-all shadow-md flex items-center justify-center gap-2"
              >
                {submittingPayment ? (
                  <><RefreshCw className="w-4 h-4 animate-spin" /> Processing...</>
                ) : (
                  <><CreditCard className="w-4 h-4" /> Record Payment</>
                )}
              </button>
            </form>
          </div>
        </div>
      )}
      </div>
      </div>
      {/* End of main flex container */}
    </>
  );
}

// Small helper component to fetch horses for an owner
function OwnerHorseSelector({ ownerId, value, onChange }) {
  const [horses, setHorses] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let mounted = true;
    axios.get(createApiUrlWithPrefix(`horses?ownerId=${ownerId}`), {
      headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
    }).then(res => {
      if (mounted) setHorses(res.data?.horses || res.data || []);
    }).catch(() => {}).finally(() => {
      if (mounted) setLoading(false);
    });
    return () => { mounted = false; };
  }, [ownerId]);
  if (loading) return <div className="text-xs text-gray-500 py-1">Loading horses...</div>;
  if (!horses.length) return <div className="text-xs text-red-500 py-1">No horses found for this owner</div>;
  return (
    <select
      value={value || ''}
      onChange={(e) => onChange(parseInt(e.target.value) || null)}
      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-green-500"
      required
    >
      <option value="">Select a horse...</option>
      {horses.map(horse => (
        <option key={horse.id} value={horse.id}>{horse.name} ({horse.breed || 'No breed'})</option>
      ))}
    </select>
  );
}

export default Owners;